import type { ListTableRow } from 'framework/ui/ui-builder';
import type { CollectionSection } from './collectionGroups';
import { PageLoadDirection } from './collectionPagerTypes';
import type { CollectionPageResponse } from './collectionPagerTypes';

export interface CollectionPage<Row> {
  rows: Row[];
  sections: CollectionSection[];
  firstPageNumber: number;
  lastPageNumber: number;
  appliedSearchValue: string;
  totalPages: number;
  totalElements: number;
  hasReachedStart: boolean;
  hasReachedEnd: boolean;
}

export const buildInitialPageState = <Row>(): CollectionPage<Row> => ({
  rows: [],
  sections: [],
  firstPageNumber: 0,
  lastPageNumber: 0,
  appliedSearchValue: '',
  totalPages: 1,
  totalElements: 0,
  hasReachedStart: false,
  hasReachedEnd: false,
});

type PageResponse<Row> = CollectionPageResponse<Row> & { sections?: CollectionSection[] };

const getNewRows = <Row extends ListTableRow>(existingRows: Row[], rows: Row[]) => {
  const ids = new Set(existingRows.map(row => row.id));
  return rows.filter(row => {
    if (ids.has(row.id)) return false;
    ids.add(row.id);
    return true;
  });
};

export const mergeCollectionPage = <Row extends ListTableRow>(
  previous: CollectionPage<Row>,
  response: PageResponse<Row>,
  direction: PageLoadDirection,
  searchValue: string,
): CollectionPage<Row> => {
  const isInitialPage = direction === PageLoadDirection.REPLACE;
  const newRows = isInitialPage ? response.result : getNewRows(previous.rows, response.result);
  const metadata = {
    sections: response.sections ?? (isInitialPage ? [] : previous.sections),
    appliedSearchValue: searchValue,
    totalPages: response.totalPages,
    totalElements: response.totalElements,
  };

  if (isInitialPage) {
    return {
      ...metadata,
      rows: newRows,
      firstPageNumber: response.pageNumber,
      lastPageNumber: response.pageNumber,
      hasReachedStart: false,
      hasReachedEnd: newRows.length === 0,
    };
  }

  if (direction === PageLoadDirection.PREVIOUS) {
    return {
      ...previous,
      ...metadata,
      rows: [...newRows, ...previous.rows],
      firstPageNumber: response.pageNumber,
      hasReachedStart: previous.hasReachedStart || newRows.length === 0,
    };
  }

  return {
    ...previous,
    ...metadata,
    rows: [...previous.rows, ...newRows],
    lastPageNumber: response.pageNumber,
    hasReachedEnd: previous.hasReachedEnd || newRows.length === 0,
  };
};

export const appendGroupedPage = <Row extends ListTableRow>(
  previous: CollectionPage<Row>,
  response: PageResponse<Row>,
  pageNumber: number,
  searchValue: string,
): CollectionPage<Row> => ({
  ...previous,
  rows: [...previous.rows, ...getNewRows(previous.rows, response.result)],
  sections: [
    ...new Map([...previous.sections, ...(response.sections ?? [])].map(section => [section.id, section])).values(),
  ],
  lastPageNumber: pageNumber,
  appliedSearchValue: searchValue,
  totalPages: response.totalPages,
  totalElements: response.totalElements,
  hasReachedStart: true,
  hasReachedEnd: true,
});
